/* eslint-disable @typescript-eslint/no-unsafe-argument */

import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostManuscriptsCreateDomCheck } from '../../../../api/manuscripts/manuscripts';
import { usePostSectionDownload } from '../../../../api/sections/sections';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import '../../../../styles/Dropdown.css';
import { executeDownload } from '../../../../utils/download';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import SpinnerModal from '../../../modules/SpinnerModal';

interface Props {
  productionId: number;
  divisionId: number;
  isReadOnlySection: boolean;
  includeVisual: boolean;
  includeIndex: boolean;
}

/* 原稿編集画面 全体出力処理用のプルダウン */
function DownloadCommand({ productionId, divisionId, isReadOnlySection, includeVisual, includeIndex }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // #region UI制御
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownListRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    const handleClickToCloseDropdown = (event: any) => {
      const element = dropdownListRef.current;
      if (!showDropdown || element?.contains(event.target)) return;
      setShowDropdown(false);
    };
    window.addEventListener('click', handleClickToCloseDropdown);
    return () => {
      window.removeEventListener('click', handleClickToCloseDropdown);
    };
  }, [showDropdown, dropdownListRef]);
  // #endregion

  // #region 全体出力
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false); // スピナーモーダルを表示する
  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: _downloadAsync,
    isError: downloadIsError,
    error: downloadError,
  } = usePostSectionDownload<AxiosError<Blob>>(optionsForBinary);

  const [displayDownloadError, setDisplayDownloadError] = useState(false);
  const [displayConfirmDownloadError, setDisplayConfirmDownloadError] = useState(false);

  useEffect(() => {
    setDisplayDownloadError(downloadIsError);
  }, [downloadIsError]);

  const [selectValue, setSelectValue] = useState<{ value: number } | null>(null);

  // ダウンロード処理
  const downloadAsync = useCallback(
    async (newValue: { value: number } | null) => {
      setDisplaySpinnerModal(true);
      if (newValue != null && productionId != null) {
        await _downloadAsync(
          {
            data: {
              document_type: newValue.value,
              production_id: productionId,
              is_visual: includeVisual,
              is_index: includeIndex,
            },
          },
          {
            onSuccess: (response) => {
              executeDownload(response);
              setDisplaySpinnerModal(false);
            },
            onError: () => {
              setDisplaySpinnerModal(false);
            },
          }
        );
      }
    },
    [productionId, _downloadAsync, includeVisual, includeIndex]
  );
  // #endregion

  // #region ダウンロード前チェック
  const options = useDefaultOptions(language);
  const { mutateAsync: downloadCheckAsync, error: downloadCheckError } = usePostManuscriptsCreateDomCheck(options);
  const [displayDownloadCheckError, setDisplayDownloadCheckError] = useState(false);
  useEffect(() => {
    setDisplayDownloadCheckError(downloadCheckError != null);
  }, [downloadCheckError]);

  const downloadClicked = useCallback(
    async (newValue: { value: number } | null) => {
      setDisplaySpinnerModal(true);
      const response = await downloadCheckAsync({ productionId });
      setDisplaySpinnerModal(false);
      if (!response.data.has_required_data) {
        setDisplayConfirmDownloadError(true);
        return;
      }
      await downloadAsync(newValue);
    },
    [productionId, downloadCheckAsync, downloadAsync]
  );
  // #endregion

  // #region その他
  const [displayConfirmSlimOutOut, setDisplayConfirmSlimOutOut] = useState(false);
  const checkHasNonSelectStatus = () => {
    const ulNode = document.getElementById('manual_tree') as HTMLInputElement;
    for (let pro = 0; pro < 2; pro += 1) {
      const className = pro === 0 ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right';
      const iNode = ulNode.getElementsByClassName(className) as HTMLCollectionOf<HTMLElement>;
      for (let i = 0; i < iNode.length; i += 1) {
        if (iNode[i].style.color === 'rgb(255, 203, 5)') {
          return true;
        }
      }
    }
    return false;
  };
  // #endregion

  return (
    <div className="dropdowncontainer">
      <span
        className="dropdownopen"
        onClick={() => !isReadOnlySection && setShowDropdown((prevState) => !prevState)}
        ref={dropdownListRef}
        style={{
          width: '100px',
          color: isReadOnlySection ? '#9d9d9d' : '',
          cursor: isReadOnlySection ? 'default' : 'pointer',
        }}
      >
        <i
          className="fa-solid fa-download"
          style={{
            color: isReadOnlySection ? '#9d9d9d' : '',
            cursor: isReadOnlySection ? 'default' : 'pointer',
          }}
        />
        {t('全体出力')}
      </span>
      {showDropdown && (
        <ul className="dropdownlist" style={{ width: '180px' }}>
          {/* 資料区分に関わらずPDF出力メニューを表示 */}
          <li
            className="dropdownitem"
            onClick={() => {
              setSelectValue({ value: 10 });
              void downloadClicked({ value: 10 });
            }}
          >
            {t('印刷用(PDF)')}
          </li>
          <li
            className="dropdownitem"
            onClick={() => {
              setSelectValue({ value: 11 });
              void downloadClicked({ value: 11 });
            }}
          >
            {t('バリデーション用(PDF)')}
          </li>
          <li
            className="dropdownitem"
            onClick={() => {
              setSelectValue({ value: 12 });
              void downloadClicked({ value: 12 });
            }}
          >
            {t('WEB用(PDF)')}
          </li>
          <li
            className="dropdownitem"
            onClick={() => {
              setSelectValue({ value: 13 });
              void downloadClicked({ value: 13 });
            }}
          >
            {t('印刷用(88%縮小北米用)')}
          </li>
          <li
            className="dropdownitem"
            onClick={() => {
              setSelectValue({ value: 17 });
              void downloadClicked({ value: 17 });
            }}
          >
            {t('分割PDF')}
          </li>
          <li
            className="dropdownitem"
            onClick={() => {
              setSelectValue({ value: 18 });
              void downloadClicked({ value: 18 });
            }}
          >
            {t('分割PDF(88%)')}
          </li>
          <li
            className="dropdownitem"
            onClick={() => {
              setSelectValue({ value: 14 });
              void downloadClicked({ value: 14 });
            }}
          >
            {t('WEB用(88%縮小北米用)')}
          </li>
          <li
            className="dropdownitem"
            onClick={() => {
              setSelectValue({ value: 15 });

              // 非抜粋配下に未指定があるかチェック
              if (checkHasNonSelectStatus()) {
                setDisplayConfirmSlimOutOut(true);
              } else {
                void downloadClicked({ value: 15 });
              }
            }}
            style={{ borderTop: 'solid 1px #e4e6e7' }}
          >
            {t('薄型OM')}
          </li>
          <li
            className="dropdownitem"
            onClick={() => {
              setSelectValue({ value: 16 });

              // 非抜粋配下に未指定があるかチェック
              if (checkHasNonSelectStatus()) {
                setDisplayConfirmSlimOutOut(true);
              } else {
                void downloadClicked({ value: 16 });
              }
            }}
          >
            {t('薄型OM(88%)')}
          </li>

          {/* (divisionId === 4 || divisionId === 10) && (
            <li
              className="dropdownitem"
              onClick={() => {
                if (!isReadOnlySection) {
                  setSelectValue({ value: 20 });
                  void downloadClicked({ value: 20 });
                }
              }}
            >
              {t('dOMパッケージ')}
            </li>
          ) */}
          {/* (divisionId === 5 || divisionId === 11) && (
            <li
              className="dropdownitem"
              onClick={() => {
                if (!isReadOnlySection) {
                  setSelectValue({ value: 30 });
                  void downloadClicked({ value: 30 });
                }
              }}
            >
              {t('WebOMパッケージ')}
            </li>
          ) */}
        </ul>
      )}
      {/* スピナーモーダル */}
      {displaySpinnerModal && <SpinnerModal />}
      {/* 原稿ダウンロードエラー */}
      {displayDownloadError && (
        <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownloadError(false)} />
      )}
      {/* 原稿ダウンロード前チェックエラー */}
      {displayDownloadCheckError && (
        <ErrorModal
          error={downloadCheckError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayDownloadCheckError(false);
          }}
        />
      )}
      {/* ダウンロード確認(エラーあり) */}
      {displayConfirmDownloadError && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmDownloadError(false)}
          onClickOk={async () => {
            setDisplayConfirmDownloadError(false);
            await downloadAsync(selectValue);
          }}
        >
          {t('エラーが存在しますが、出力しますか？')}
        </ConfirmModal>
      )}
      {/* 出力前の抜粋状態確認メッセージ */}
      {displayConfirmSlimOutOut && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmSlimOutOut(false)}
          onClickOk={() => {
            setDisplayConfirmSlimOutOut(false);
            void downloadClicked({ value: Number(selectValue?.value) });
          }}
        >
          <div>{t('「非抜粋」のセクション配下に「未指定」もしくは「抜粋」のタイトルが存在しますが実行しますか？')}</div>
        </ConfirmModal>
      )}
    </div>
  );
}
export default DownloadCommand;
